import type { CSSProperties, FC, ReactNode } from 'react'
import type { Drawing, Pt, StrokeWeight } from '@/lib/drawings/types'

import { useMemo } from 'react'
import { mm } from '@/lib/drawings/types'

export const SHEET_WIDTH = 1000
export const SHEET_HEIGHT = 600

const INK = '#ECE6D8'
const MUTE = '#8A8478'
const SIGNAL = '#86B6F0'

export interface SheetLayout {
    scale: number
    toScreen: (point: Pt) => Pt
}

/**
 * Fits a drawing into the fixed sheet, leaving room for level labels, bubbles and the scale bar.
 */
export const layoutDrawing = (drawing: Drawing): SheetLayout => {
    let minX = Infinity
    let minY = Infinity
    let maxX = -Infinity
    let maxY = -Infinity

    const add = ([x, y]: Pt): void => {
        minX = Math.min(minX, x)
        maxX = Math.max(maxX, x)
        minY = Math.min(minY, y)
        maxY = Math.max(maxY, y)
    }

    drawing.strokes.forEach(stroke => stroke.points.forEach(add))
    drawing.grids.forEach(grid => {
        add(grid.from)
        add(grid.to)
    })
    drawing.levels.forEach(level => add(level.at))
    drawing.notes.forEach(note => add(note.at))
    drawing.dims.forEach(dim => {
        const vertical = Math.abs(dim.b[1] - dim.a[1]) > Math.abs(dim.b[0] - dim.a[0])
        const n: Pt = vertical ? [dim.offset, 0] : [0, dim.offset]

        add([dim.a[0] + n[0], dim.a[1] + n[1]])
        add([dim.b[0] + n[0], dim.b[1] + n[1]])
    })

    if (drawing.north) add(drawing.north)

    const padLeft = 40
    const padRight = drawing.levels.length > 0 ? 90 : 40
    const padTop = 40
    const padBottom = 64
    const spanX = Math.max(1, maxX - minX)
    const spanY = Math.max(1, maxY - minY)
    const scale = Math.min((SHEET_WIDTH - padLeft - padRight) / spanX, (SHEET_HEIGHT - padTop - padBottom) / spanY)
    const offsetX = padLeft + ((SHEET_WIDTH - padLeft - padRight) - spanX * scale) / 2
    const offsetY = padTop + ((SHEET_HEIGHT - padTop - padBottom) - spanY * scale) / 2

    return {
        scale,
        toScreen: ([x, y]: Pt): Pt => [offsetX + (x - minX) * scale, offsetY + (maxY - y) * scale]
    }
}

const STROKE: Record<StrokeWeight, { colour: string, width: number, dash?: string }> = {
    cut: { colour: INK, width: 1.6 },
    line: { colour: INK, width: 0.9 },
    thin: { colour: MUTE, width: 0.6 },
    hidden: { colour: MUTE, width: 0.6, dash: '4 3' },
    accent: { colour: SIGNAL, width: 1.2 }
}

const path = (points: Array<Pt>, closed: boolean = false): string => `${points.map(([x, y], index) => `${index === 0 ? 'M' : 'L'}${x.toFixed(1)} ${y.toFixed(1)}`).join('')}${closed ? 'Z' : ''}`

const delay = (value: number): CSSProperties => ({ '--delay': `${value}ms` } as CSSProperties)

const niceBar = (pixelsPerMetre: number): number => {
    const target = 150 / pixelsPerMetre
    const options = [1, 2, 5, 10, 20, 50]

    return options.reduce((best, value) => (Math.abs(value - target) < Math.abs(best - target) ? value : best), options[0])
}

interface DrawingSheetProps {
    drawing: Drawing
    animate?: boolean
}

/**
 * One drawing as fine SVG lines, with dimension lines, grid bubbles, level marks, notes, a north
 * arrow and a scale bar. Strokes draw themselves in when the parent's data-reveal turns "in".
 */
export const DrawingSheet: FC<DrawingSheetProps> = ({ drawing, animate = true }): ReactNode => {
    const layout = useMemo(() => layoutDrawing(drawing), [drawing])
    const { toScreen, scale } = layout
    const strokeClass = animate ? 'draw-path' : undefined
    const fadeClass = animate ? 'reveal-cell' : undefined
    const bar = niceBar(scale)
    const barPx = bar * scale

    return (
        <g>
            {drawing.grids.map((grid, index) => {
                const [x1, y1] = toScreen(grid.from)
                const [x2, y2] = toScreen(grid.to)
                const length = Math.hypot(x2 - x1, y2 - y1) || 1
                const ux = (x2 - x1) / length
                const uy = (y2 - y1) / length
                const cx = x2 + ux * 11
                const cy = y2 + uy * 11

                return (
                    <g key={`grid-${index}`} className={fadeClass} style={delay(80 + index * 30)}>
                        <line x1={x1} y1={y1} x2={x2} y2={y2} stroke='#3A362F' strokeWidth={0.6} strokeDasharray='10 3 2 3' />
                        <circle cx={cx} cy={cy} r={10} fill='#0A0908' stroke={MUTE} strokeWidth={0.8} />
                        <text x={cx} y={cy} dy='0.35em' textAnchor='middle' fill={INK} className='font-mono text-[9px]'>
                            {grid.label}
                        </text>
                    </g>
                )
            })}

            {drawing.strokes.map((stroke, index) => {
                const style = STROKE[stroke.weight]
                const d = path(stroke.points.map(toScreen), stroke.closed)
                const dashed = Boolean(style.dash)

                return (
                    <path
                        key={`stroke-${index}`}
                        d={d}
                        fill='none'
                        stroke={style.colour}
                        strokeWidth={style.width}
                        strokeDasharray={style.dash}
                        strokeLinejoin='miter'
                        pathLength={dashed ? undefined : 1}
                        className={dashed ? fadeClass : strokeClass}
                        style={delay(Math.min(1400, 120 + index * 9))}
                    />
                )
            })}

            {drawing.dims.map((dim, index) => {
                const vertical = Math.abs(dim.b[1] - dim.a[1]) > Math.abs(dim.b[0] - dim.a[0])
                const n: Pt = vertical ? [dim.offset, 0] : [0, dim.offset]
                const a = toScreen(dim.a)
                const b = toScreen(dim.b)
                const a2 = toScreen([dim.a[0] + n[0], dim.a[1] + n[1]])
                const b2 = toScreen([dim.b[0] + n[0], dim.b[1] + n[1]])
                const length = Math.hypot(b[0] - a[0], b[1] - a[1])
                const label = dim.label ?? mm(Math.hypot(dim.b[0] - dim.a[0], dim.b[1] - dim.a[1]))
                const mx = (a2[0] + b2[0]) / 2
                const my = (a2[1] + b2[1]) / 2
                const sx = Math.sign(a2[0] - a[0]) || 0
                const sy = Math.sign(a2[1] - a[1]) || 0

                return (
                    <g key={`dim-${index}`} className={fadeClass} style={delay(900 + index * 60)}>
                        <line x1={a[0] + sx * 4} y1={a[1] + sy * 4} x2={a2[0] + sx * 4} y2={a2[1] + sy * 4} stroke={MUTE} strokeWidth={0.6} />
                        <line x1={b[0] + sx * 4} y1={b[1] + sy * 4} x2={b2[0] + sx * 4} y2={b2[1] + sy * 4} stroke={MUTE} strokeWidth={0.6} />
                        <line x1={a2[0]} y1={a2[1]} x2={b2[0]} y2={b2[1]} stroke={MUTE} strokeWidth={0.7} />
                        <path d={`M${a2[0] - 4} ${a2[1] + 4}L${a2[0] + 4} ${a2[1] - 4}M${b2[0] - 4} ${b2[1] + 4}L${b2[0] + 4} ${b2[1] - 4}`} stroke={INK} strokeWidth={1} />

                        {length > 28 && (
                            <text
                                x={mx}
                                y={my}
                                dy={vertical ? '0.35em' : '-0.5em'}
                                dx={vertical ? (sx >= 0 ? 6 : -6) : 0}
                                textAnchor={vertical ? (sx >= 0 ? 'start' : 'end') : 'middle'}
                                fill={INK}
                                className='font-mono text-[9px] tracking-[0.04em]'
                            >
                                {label}
                            </text>
                        )}
                    </g>
                )
            })}

            {drawing.levels.map((level, index) => {
                const [x, y] = toScreen(level.at)

                return (
                    <g key={`level-${index}`} className={fadeClass} style={delay(1000 + index * 50)}>
                        <path d={`M${x} ${y}L${x - 5} ${y - 7}L${x + 5} ${y - 7}Z`} fill='none' stroke={INK} strokeWidth={0.8} />
                        <line x1={x} y1={y} x2={x + 44} y2={y} stroke={MUTE} strokeWidth={0.6} />
                        <text x={x + 8} y={y - 3} fill={INK} className='font-mono text-[9px]'>
                            {level.label}
                        </text>
                    </g>
                )
            })}

            {drawing.notes.map((note, index) => {
                const [x, y] = toScreen(note.at)
                const fill = note.tone === 'signal' ? SIGNAL : note.tone === 'ink' ? INK : MUTE

                return (
                    <text
                        key={`note-${index}`}
                        x={x}
                        y={y}
                        textAnchor={note.anchor ?? 'start'}
                        fill={fill}
                        className={`${fadeClass ?? ''} font-mono text-[10px] uppercase tracking-[0.1em]`}
                        style={delay(1100 + index * 60)}
                    >
                        {note.text}
                    </text>
                )
            })}

            {drawing.north && (() => {
                const [x, y] = toScreen(drawing.north)

                return (
                    <g className={fadeClass} style={delay(1200)}>
                        <circle cx={x} cy={y} r={14} fill='none' stroke={MUTE} strokeWidth={0.7} />
                        <path d={`M${x} ${y - 12}L${x + 5} ${y + 6}L${x} ${y + 2}L${x - 5} ${y + 6}Z`} fill={INK} />
                        <text x={x} y={y - 18} textAnchor='middle' fill={INK} className='font-mono text-[9px]'>
                            N
                        </text>
                    </g>
                )
            })()}

            <g className={fadeClass} style={delay(1250)}>
                {[0, 1, 2, 3].map(segment => (
                    <rect
                        key={segment}
                        x={40 + (barPx / 4) * segment}
                        y={SHEET_HEIGHT - 30}
                        width={barPx / 4}
                        height={5}
                        fill={segment % 2 === 0 ? INK : 'none'}
                        stroke={INK}
                        strokeWidth={0.7}
                    />
                ))}

                <text x={40} y={SHEET_HEIGHT - 36} fill={MUTE} className='font-mono text-[9px]'>
                    0
                </text>

                <text x={40 + barPx} y={SHEET_HEIGHT - 36} textAnchor='middle' fill={MUTE} className='font-mono text-[9px]'>
                    {bar} m
                </text>

                <text x={40 + barPx + 24} y={SHEET_HEIGHT - 25} fill={MUTE} className='font-mono text-[9px] tracking-[0.08em]'>
                    SCALE {drawing.scale}
                </text>
            </g>
        </g>
    )
}
